import { useState, useRef, useEffect } from 'react';
import { PawPrint } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AddPetModal } from './AddPetModal';
import { useAddPetModal } from '../context/AddPetModalContext';

interface NoPetsEmptyStateProps {
  title?: string;
  description?: string;
  buttonLabel?: string;
  className?: string;
}

export function NoPetsEmptyState({
  title,
  description,
  buttonLabel,
  className = '',
}: NoPetsEmptyStateProps) {
  const [showAddPet, setShowAddPet] = useState(false);
  const mountedRef = useRef(true);
  const { t } = useTranslation();
  const addPetModal = useAddPetModal();

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const displayTitle = title ?? t('emptyState.noPets.title');
  const displayDescription = description ?? t('emptyState.noPets.description');
  const displayButtonLabel = buttonLabel ?? t('emptyState.noPets.button');

  const handleAddPetClick = () => {
    if (addPetModal) {
      addPetModal.openAddPet();
    } else {
      setShowAddPet(true);
    }
  };

  return (
    <div
      className={`flex flex-col items-center justify-center min-h-[400px] text-center p-8 ${className}`}
    >
      <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/40 dark:to-purple-900/40 rounded-full flex items-center justify-center mb-6">
        <PawPrint className="w-12 h-12 text-blue-600 dark:text-blue-400" />
      </div>
      <h2 className="text-2xl font-semibold mb-2 dark:text-white">
        {displayTitle}
      </h2>
      <p className="text-gray-600 dark:text-white/70 mb-6 max-w-md">
        {displayDescription}
      </p>
      <button
        onClick={handleAddPetClick}
        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        {displayButtonLabel}
      </button>

      {!addPetModal && showAddPet && (
        <AddPetModal
          onClose={() => {
            // Defer to avoid setState during commit (modal may call onClose when unmounting → max update depth)
            setTimeout(() => {
              if (mountedRef.current) setShowAddPet(false);
            }, 0);
          }}
        />
      )}
    </div>
  );
}
